import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSearchHighlight } from '../hooks/useSearchHighlight';
import { useTheme } from '../context/ThemeContext';
import { getColors } from '../styles/colors';
import homeData from '../data/pages/home.json';
import OptimizedImage from '../components/OptimizedImage';
import * as S from '../styles/pages/Home.styles';
import * as LayoutS from '../styles/components/Layout.styles';

// Lotus Video Component
const LotusVideo: React.FC<{ language: 'de' | 'en' }> = ({ language }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [hasStarted, setHasStarted] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [lastFrameDataUrl, setLastFrameDataUrl] = useState<string | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const video = videoRef.current;
    const container = containerRef.current;
    const canvas = canvasRef.current;
    
    if (!video || !container) return;

    // If already finished, just show canvas and hide video - don't set up any listeners
    if (isFinished) {
      video.style.display = 'none';
      video.pause();
      if (canvas) {
        canvas.style.display = 'block';
      }
      return;
    }

    // Handle video end - capture last frame and display as image
    const handleEnded = () => {
      if (video && !isFinished && canvas) {
        // Immediately pause and seek to exact end
        video.pause();
        video.currentTime = video.duration;
        
        let frameCaptured = false;
        
        // Wait for video to seek to the last frame
        const handleSeeked = () => {
          if (frameCaptured || !video || !canvas || isFinished) return;
          
          // Double-check that we're really at the end
          if (Math.abs(video.currentTime - video.duration) > 0.1) {
            // Not at the end yet, try again
            video.currentTime = video.duration;
            return;
          }
          
          frameCaptured = true;
          
          // Set canvas size to match video (use devicePixelRatio for better quality)
          const dpr = window.devicePixelRatio || 1;
          canvas.width = video.videoWidth * dpr;
          canvas.height = video.videoHeight * dpr;
          
          // Draw the last frame to canvas and convert to data URL
          const ctx = canvas.getContext('2d');
          if (ctx) {
            ctx.scale(dpr, dpr);
            ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);
            // Convert canvas to data URL for static image (use higher quality)
            const dataUrl = canvas.toDataURL('image/png', 1.0);
            setLastFrameDataUrl(dataUrl);
            
            // Immediately hide video and show canvas to prevent flickering
            video.style.display = 'none';
            video.pause();
            video.currentTime = video.duration;
            canvas.style.display = 'block';
            
            // Remove all event listeners
            video.removeEventListener('ended', handleEnded);
            video.removeEventListener('timeupdate', handleTimeUpdate);
            video.removeEventListener('seeked', handleSeeked);
            
            setIsFinished(true);
          }
        };
        
        // Use seeked event to ensure we're at the exact last frame
        video.addEventListener('seeked', handleSeeked, { once: true });
        
        // Multiple fallbacks to ensure we capture the frame
        // First fallback: immediate check
        setTimeout(() => {
          if (!frameCaptured && !isFinished && video && canvas) {
            handleSeeked();
          }
        }, 50);
        
        // Second fallback: after seeked should have fired
        setTimeout(() => {
          if (!frameCaptured && !isFinished && video && canvas) {
            // Force seek again
            video.currentTime = video.duration;
            setTimeout(() => {
              if (!frameCaptured && !isFinished && video && canvas) {
                handleSeeked();
              }
            }, 50);
          }
        }, 150);
      }
    };

    // Handle timeupdate - let video play until the very end
    const handleTimeUpdate = () => {
      if (video && !isFinished) {
        // Only pause if we're very close to the end and video hasn't ended naturally
        if (video.currentTime >= video.duration - 0.01 && video.readyState >= 2) {
          // Don't pause here, let it end naturally to trigger 'ended' event
        }
      }
    };

    video.addEventListener('ended', handleEnded);
    video.addEventListener('timeupdate', handleTimeUpdate);

    // Intersection Observer to detect when video is in view
    if (!hasStarted && !isFinished) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
             if (entry.isIntersecting && !hasStarted && !isFinished) {
               // Play at normal speed (1x)
               video.playbackRate = 1.0;
               video.play().catch((error) => {
                 console.log('Video play error:', error);
               });
               setHasStarted(true);
              // Disconnect observer after starting to play
              if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = null;
              }
            }
          });
        },
        { threshold: 0.5 } // Trigger when 50% of video is visible
      );

      observerRef.current.observe(container);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [hasStarted, isFinished]);

  return (
    <S.VideoContainer
      ref={containerRef}
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: 0.2 }}
    >
      {isFinished && lastFrameDataUrl ? (
        <img
          src={lastFrameDataUrl}
          alt={homeData[language as 'de' | 'en']['yoga-outdoor'].lotusVideo.alt}
          className="lotus-media"
        />
      ) : (
        <>
          <video
            ref={videoRef}
            src={homeData[language as 'de' | 'en']['yoga-outdoor'].lotusVideo.src}
            playsInline
            muted
            preload="auto"
            loop={false}
            className="lotus-media"
          />
          <canvas
            ref={canvasRef}
            className="lotus-media"
            style={{ display: 'none' }}
          />
        </>
      )}
    </S.VideoContainer>
  );
};

const Home: React.FC = () => {
  const { isDarkMode } = useTheme();
  const colors = getColors(isDarkMode);
  const [isScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showPngLogo, setShowPngLogo] = useState(false);
  const [gifKey] = useState(0);
  const logoGifRef = useRef<HTMLImageElement>(null);
  useSearchHighlight();
  
  const [language, setLanguage] = useState<'de' | 'en'>(() => {
    const stored = localStorage.getItem('siteLanguage');
    return (stored === 'en' || stored === 'de') ? stored : 'de';
  });
  
  // Handle GIF logo animation - switch to static logo after GIF finishes playing once
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowPngLogo(true);
    }, 2400);
    return () => clearTimeout(timer);
  }, [gifKey]);
  
  useEffect(() => {
    const handleLanguageChange = (e: CustomEvent | StorageEvent) => {
      const newLang = e instanceof CustomEvent ? e.detail : localStorage.getItem('siteLanguage');
      if (newLang === 'en' || newLang === 'de') {
        setLanguage(newLang);
      }
    };
    
    window.addEventListener('languageChanged', handleLanguageChange as EventListener);
    window.addEventListener('storage', handleLanguageChange as EventListener);
    
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange as EventListener);
      window.removeEventListener('storage', handleLanguageChange as EventListener);
    };
  }, []);
  
  const data = homeData[language];

  useEffect(() => {
    const handleScroll = () => {
      // Text-In-View Detection für Animationen
      const introSection = document.getElementById('intro');
      if (introSection) {
        const rect = introSection.getBoundingClientRect();
        const isInView = rect.top < window.innerHeight * 0.8 && rect.bottom > 0;
        
        // Add/remove class for animations
        const textElements = introSection.querySelectorAll('p');
        textElements.forEach((el, index) => {
          if (isInView) {
            setTimeout(() => {
              el.classList.add('in-view');
            }, index * 200);
          }
        });
      }
      
      // Parallax-Effekt für Mobile: Bild wird beim Scrollen sichtbar
      if (window.innerWidth <= 968) {
        if (introSection) {
          const rect = introSection.getBoundingClientRect();
          const sectionTop = rect.top;
          const sectionHeight = rect.height;
          const viewportHeight = window.innerHeight;
          
          // Berechne Scroll-Progress innerhalb der Section
          const progress = Math.max(0, Math.min(1, (-sectionTop) / (sectionHeight - viewportHeight)));
          setScrollProgress(progress);
        }
      } else {
        setScrollProgress(0);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial call
    
    // Intersection Observer für bessere Performance
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const textElements = entry.target.querySelectorAll('p');
            textElements.forEach((el, index) => {
              setTimeout(() => {
                el.classList.add('in-view');
              }, index * 200);
            });
          }
        });
      },
      { threshold: 0.3, rootMargin: '-50px' }
    );
    
    const introSection = document.getElementById('intro');
    if (introSection) {
      observer.observe(introSection);
    }
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (introSection) {
        observer.unobserve(introSection);
      }
    };
  }, []);

  return (
    <S.PageWrapper colors={colors}>
      <S.HeroSection id="home" isScrolled={isScrolled}>
        <LayoutS.HeaderVideoContainer isScrolled={isScrolled} colors={colors}>
          <OptimizedImage
            src="/yoga/yoga_1_mobile.jpg"
            alt="Kamana Yoga"
            className="visible desktop-image"
            sizes="100vw"
            width={3560}
            height={2478}
            loading="eager"
            fetchPriority="high"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center' }}
          />
          <OptimizedImage
            src="/yoga/yoga_1_mobile.jpg"
            alt="Kamana Yoga"
            className="visible mobile-image"
            sizes="100vw"
            width={3560}
            height={2478}
            loading="eager"
            fetchPriority="high"
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'left center' }}
          />
        </LayoutS.HeaderVideoContainer>
        {!isScrolled && (
          <>
            <LayoutS.HeroContent
              colors={colors}
              isDarkMode={isDarkMode}
              isScrolled={isScrolled}
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ 
                duration: 1.2, 
                delay: 0.3,
                ease: [0.16, 1, 0.3, 1]
              }}
              style={{ x: 0 }}
            >
              <motion.div 
                style={{ height: 'clamp(100px, 12vw, 200px)', marginBottom: '-10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                initial={{ opacity: 0, y: 20, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
              >
                {showPngLogo ? (
                  <LayoutS.HeroLogoDesktopPng
                    src="/yoga/kamana-logo.png"
                    alt="Kamana Yoga Logo"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                  />
                ) : (
                  <LayoutS.HeroLogoDesktopGif
                    ref={logoGifRef}
                    key={gifKey}
                    src="/yoga/kamana-logo.gif"
                    alt="Kamana Yoga Logo"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                  />
                )}
              </motion.div>
              <LayoutS.HeroTitle 
                colors={colors}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.7, ease: [0.16, 1, 0.3, 1] }}
              >
                {homeData[language].hero.title}
              </LayoutS.HeroTitle>
              <LayoutS.HeroSubtitle 
                colors={colors} 
                className="hero-subtitle-content"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.9, ease: [0.16, 1, 0.3, 1] }}
              >
                <span className="desktop-text">
                  {homeData[language].hero.subtitle}
                  {'\n'}
                  {homeData[language].hero.location}
                  {'\n'}
                  {'\n'}
                  {homeData[language].hero.tagline}
                </span>
                <span className="mobile-text">
                  {homeData[language].hero.subtitle}
                  {'\n'}
                  {homeData[language].hero.location}
                  {'\n\n'}
                  {homeData[language].hero.tagline}
                </span>
              </LayoutS.HeroSubtitle>
              <LayoutS.CTAButton
                colors={colors}
                type="button"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const element = document.querySelector('#yoga-online');
                  if (element) {
                    const headerHeight = 0;
                    const extraOffset = -30;
                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - headerHeight - extraOffset;
                    window.scrollTo({
                      top: Math.max(0, offsetPosition),
                      behavior: 'smooth'
                    });
                  }
                  return false;
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {language === 'de' ? 'Jetzt starten' : 'Start now'}
              </LayoutS.CTAButton>
            </LayoutS.HeroContent>
            <LayoutS.MobileContentContainer
              initial={{ opacity: 1 }}
              animate={{ opacity: 1 }}
            >
              <div style={{ height: 'clamp(120px, 12vw, 120px)', marginBottom: '-10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {showPngLogo ? (
                  <LayoutS.HeroLogoMobilePng
                    src="/yoga/kamana-logo.png"
                    alt="Kamana Yoga Logo"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                  />
                ) : (
                  <LayoutS.HeroLogoMobileGif
                    ref={logoGifRef}
                    key={gifKey}
                    src="/yoga/kamana-logo.gif"
                    alt="Kamana Yoga Logo"
                    initial={{ opacity: 1 }}
                    animate={{ opacity: 1 }}
                  />
                )}
              </div>
              <LayoutS.MobileTagline 
                colors={colors}
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
              >
                {'  '}Be like water, just flow.
              </LayoutS.MobileTagline>
              <LayoutS.MobileHeroSubtitle 
                colors={colors}
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
              >
                {homeData[language].hero.subtitle}
                {'\n'}
                {homeData[language].hero.location}
              </LayoutS.MobileHeroSubtitle>
              <LayoutS.MobileCTAButton
                colors={colors}
                type="button"
                initial={{ opacity: 1 }}
                animate={{ opacity: 1 }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const element = document.querySelector('#yoga-online');
                  if (element) {
                    const headerHeight = 0;
                    const extraOffset = -30;
                    const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                    const offsetPosition = elementPosition - headerHeight - extraOffset;
                    window.scrollTo({
                      top: Math.max(0, offsetPosition),
                      behavior: 'smooth'
                    });
                  }
                  return false;
                }}
                onMouseDown={(e) => {
                  e.stopPropagation();
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {language === 'de' ? 'Jetzt starten' : 'Start now'}
              </LayoutS.MobileCTAButton>
            </LayoutS.MobileContentContainer>
          </>
        )}
      </S.HeroSection>

      {/* Intro Section - Split Layout */}
      <S.SplitSection
        id="intro"
        tabIndex={-1}
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <S.SplitLeft bgColor="#E6007A" scrollProgress={scrollProgress}>
          <S.SplitContent textColor="white">
            <motion.p
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.1,
                ease: [0.4, 0, 0.2, 1]
              }}
              className="mobile-text-body"
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.85rem, 1.5vw, 1.2rem)',
                lineHeight: 'clamp(1.4, 2vw, 1.7)',
                marginBottom: 'clamp(20px, 3vw, 30px)'
              }}
            >
              {data.intro.text1}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.4,
                ease: [0.4, 0, 0.2, 1]
              }}
              className="mobile-text-body"
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.85rem, 1.5vw, 1.2rem)',
                lineHeight: 'clamp(1.4, 2vw, 1.7)',
                marginBottom: 'clamp(20px, 3vw, 30px)'
              }}
            >
              {data.intro.text2}
            </motion.p>
          </S.SplitContent>
        </S.SplitLeft>
        <S.SplitRight scrollProgress={scrollProgress}>
          <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              duration: 1, 
              delay: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
            style={{ 
              width: '100%', 
              height: '100%',
              display: 'flex',
              alignItems: 'stretch'
            }}
          >
            <OptimizedImage
              src={data.intro.image.src}
              alt={data.intro.image.alt}
              className="desktop-image"
              sizes={data.intro.image.sizes.desktop}
              width={data.intro.image.width}
              height={data.intro.image.height}
              loading={data.intro.image.loading as 'lazy' | 'eager'}
              fetchPriority={data.intro.image.fetchPriority as 'high' | 'low' | 'auto'}
            />
            <OptimizedImage
              src={data.intro.image.src}
              alt={data.intro.image.alt}
              className="mobile-image"
              sizes={data.intro.image.sizes.mobile}
              width={data.intro.image.width}
              height={data.intro.image.height}
              loading={data.intro.image.loading as 'lazy' | 'eager'}
              fetchPriority={data.intro.image.fetchPriority as 'high' | 'low' | 'auto'}
            />
          </motion.div>
        </S.SplitRight>
      </S.SplitSection>

      {/* Yoga Outdoor Section - Mit Bild links */}
      <S.SplitSectionNormal
        id="yoga-outdoor"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <S.SplitLeft bgColor="transparent" scrollProgress={scrollProgress} noPadding={true}>
          <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              duration: 1, 
              delay: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
            style={{ 
              width: '100%', 
              height: '100%',
              display: 'flex',
              alignItems: 'stretch'
            }}
          >
            <OptimizedImage
              src={data['yoga-outdoor'].image.src}
              alt={data['yoga-outdoor'].image.alt}
              className="desktop-image"
              sizes={data['yoga-outdoor'].image.sizes.desktop}
              width={data['yoga-outdoor'].image.width}
              height={data['yoga-outdoor'].image.height}
              loading={data['yoga-outdoor'].image.loading as 'lazy' | 'eager'}
            />
            <OptimizedImage
              src={data['yoga-outdoor'].image.src}
              alt={data['yoga-outdoor'].image.alt}
              className="mobile-image"
              sizes={data['yoga-outdoor'].image.sizes.mobile}
              width={data['yoga-outdoor'].image.width}
              height={data['yoga-outdoor'].image.height}
              loading={data['yoga-outdoor'].image.loading as 'lazy' | 'eager'}
            />
          </motion.div>
        </S.SplitLeft>
        <S.SplitRightWithBg scrollProgress={scrollProgress} bgColor="white" className="yoga-outdoor-section">
          <S.SplitContent textColor="#333">
            {data['yoga-outdoor'].title && (
              <motion.p
                className="mobile-text-small"
                initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
                whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ 
                  duration: 0.9, 
                  delay: 0.1,
                  ease: [0.4, 0, 0.2, 1]
                }}
                style={{
                  transformStyle: 'preserve-3d',
                  perspective: '1000px',
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                  lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                  marginBottom: 'clamp(20px, 3vw, 30px)',
                  color: '#333',
                  fontWeight: 400
                }}
              >
                {data['yoga-outdoor'].title}
              </motion.p>
            )}
            <motion.p
              className="mobile-text-small"
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.4,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                color: '#333',
                fontWeight: 400,
                whiteSpace: 'pre-line'
              }}
            >
              {data['yoga-outdoor'].description}
            </motion.p>
            
            {/* Lotus Video */}
            <LotusVideo language={language} />

            <motion.p
              className="mobile-text-small"
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.7,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                color: '#333',
                fontWeight: 400,
                whiteSpace: 'pre-line'
              }}
            >
              {data['yoga-outdoor'].experience}
            </motion.p>
            <motion.h3
              className="mobile-text-small"
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 1.0,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                marginTop: 'clamp(30px, 4vw, 50px)',
                color: '#e80c7f',
                fontWeight: 700
              }}
            >
                {data['yoga-outdoor'].dates}
              </motion.h3>
               <motion.button
                  className="mobile-text-small"
                  onClick={() => {
                    const element = document.querySelector('#contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
                    fontWeight: 600,
                    color: 'white',
                  background: '#e80c7f',
                  border: 'none',
                  borderRadius: '12px',
                  padding: 'clamp(10px, 1.5vw, 14px) clamp(20px, 3vw, 28px)',
                  cursor: 'pointer',
                  marginTop: 'clamp(20px, 3vw, 30px)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  outline: 'none',
                  display: 'inline-block',
                  position: 'relative',
                  overflow: 'hidden'
                }}
                whileHover={{ 
                  y: -3,
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
                whileTap={{ 
                  scale: 0.98,
                  transition: { duration: 0.1 }
                }}
              >
                {data['yoga-outdoor'].buttonText}
              </motion.button>
          </S.SplitContent>
        </S.SplitRightWithBg>
      </S.SplitSectionNormal>

      {/* Online Yoga Section - Split Layout wie Intro */}
      <S.SplitSectionNormal
        id="yoga-online"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <S.SplitLeft bgColor="#E6007A" scrollProgress={scrollProgress}>
          <S.SplitContent textColor="white">
            <motion.p
              className="mobile-text-small"
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.1,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                color: 'white',
                fontWeight: 400
              }}
            >
              {data['yoga-online'].title}
            </motion.p>
            <motion.p
              className="mobile-text-small"
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.4,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                color: 'white',
                fontWeight: 400
              }}
            >
              {data['yoga-online'].subtitle}{' '}
              <span style={{ fontWeight: 700 }}>
                {data['yoga-online'].highlight}
              </span>
            </motion.p>
            <motion.p
              className="mobile-text-small"
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.7,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                color: 'white',
                fontWeight: 400,
                whiteSpace: 'pre-line'
              }}
            >
              {data['yoga-online'].description}
            </motion.p>
            <motion.p
              className="mobile-text-small"
              initial={{ opacity: 0, y: 40, scale: 0.9, rotateX: -15 }}
              whileInView={{ opacity: 1, y: 0, scale: 1, rotateX: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 1.0,
                ease: [0.4, 0, 0.2, 1]
              }}
              style={{
                transformStyle: 'preserve-3d',
                perspective: '1000px',
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                marginBottom: 'clamp(20px, 3vw, 30px)',
                color: 'white',
                fontWeight: 400,
                whiteSpace: 'pre-line'
              }}
            >
              {data['yoga-online'].features.map((feature: string, index: number) => (
                <React.Fragment key={index}>
                  {feature}
                  {index < data['yoga-online'].features.length - 1 && '\n'}
                </React.Fragment>
              ))}
            </motion.p>
             <motion.button
              className="mobile-text-small"
              onClick={() => {
                const element = document.querySelector('#contact');
                if (element) {
                  element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
                fontWeight: 600,
                color: '#e80c7f',
                background: 'white',
                border: 'none',
                borderRadius: '12px',
                padding: 'clamp(10px, 1.5vw, 14px) clamp(20px, 3vw, 28px)',
                cursor: 'pointer',
                marginTop: 'clamp(20px, 3vw, 30px)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                outline: 'none',
                display: 'inline-block',
                position: 'relative',
                overflow: 'hidden'
              }}
              whileHover={{ 
                y: -3,
                scale: 1.05,
                transition: { duration: 0.3 }
              }}
              whileTap={{ 
                scale: 0.98,
                transition: { duration: 0.1 }
              }}
            >
              {data['yoga-online'].cta}
            </motion.button>
          </S.SplitContent>
        </S.SplitLeft>
        <S.SplitRight scrollProgress={scrollProgress}>
          <motion.div
            initial={{ opacity: 0, scale: 1.1 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ 
              duration: 1, 
              delay: 0.2,
              ease: [0.4, 0, 0.2, 1]
            }}
            style={{ 
              width: '100%', 
              height: '100%',
              display: 'flex',
              alignItems: 'stretch'
            }}
          >
            <OptimizedImage
              src={data['yoga-online'].image.src}
              alt={data['yoga-online'].image.alt}
              className="desktop-image"
              sizes={data['yoga-online'].image.sizes.desktop}
              width={data['yoga-online'].image.width}
              height={data['yoga-online'].image.height}
              loading={data['yoga-online'].image.loading as 'lazy' | 'eager'}
            />
            <OptimizedImage
              src={data['yoga-online'].image.src}
              alt={data['yoga-online'].image.alt}
              className="mobile-image"
              sizes={data['yoga-online'].image.sizes.mobile}
              width={data['yoga-online'].image.width}
              height={data['yoga-online'].image.height}
              loading={data['yoga-online'].image.loading as 'lazy' | 'eager'}
            />
          </motion.div>
        </S.SplitRight>
      </S.SplitSectionNormal>

      {/* Testimonials Section - Weißer Hintergrund */}
      <S.Section
        id="testimonials"
        style={{ 
          background: 'white',
          padding: '80px 0',
          marginTop: '0'
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <S.Container>
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            style={{ marginBottom: 'clamp(40px, 5vw, 60px)' }}
          >
            <motion.p
              className="mobile-text-small"
              style={{
                fontFamily: "'Poppins', sans-serif",
                fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                color: '#333',
                fontWeight: 400,
                textAlign: 'center',
                marginBottom: 'clamp(50px, 6vw, 80px)'
              }}
            >
              {data.testimonials.motto}
            </motion.p>
          </motion.div>
          
          <S.TestimonialsGrid>
            {data.testimonials.items.map((testimonial: any, index: number) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{
                  background: '#f8f8f8',
                  padding: 'clamp(25px, 3vw, 35px)',
                  borderRadius: '12px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  textAlign: 'center'
                }}
                whileHover={{
                  y: -5,
                  transition: { duration: 0.3 }
                }}
              >
                {/* Profilfoto */}
                <img
                  src={testimonial.avatar || `https://i.pravatar.cc/150?img=${index + 1}`}
                  alt={testimonial.author}
                  style={{
                    width: 'clamp(60px, 8vw, 80px)',
                    height: 'clamp(60px, 8vw, 80px)',
                    borderRadius: '50%',
                    objectFit: 'cover',
                    marginBottom: 'clamp(15px, 2vw, 20px)',
                    border: '3px solid #e80c7f'
                  }}
                />
                
                {/* Sterne */}
                <div style={{
                  display: 'flex',
                  gap: '4px',
                  marginBottom: 'clamp(15px, 2vw, 20px)'
                }}>
                  {[...Array(5)].map((_, i) => (
                    <span
                      key={i}
                      style={{
                        color: i < (testimonial.rating || 5) ? '#FFD700' : '#ddd',
                        fontSize: 'clamp(16px, 2vw, 20px)'
                      }}
                    >
                      ★
                    </span>
                  ))}
                </div>
                
                {/* Text */}
                <p
                  className="mobile-text-small"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                    lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                    color: '#333',
                    fontWeight: 400,
                    fontStyle: 'italic',
                    marginBottom: 'clamp(15px, 2vw, 20px)',
                    flex: 1
                  }}
                >
                  "{testimonial.text}"
                </p>
                
                {/* Autor */}
                <p
                  className="mobile-text-small"
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                    color: '#666',
                    fontWeight: 600,
                    marginTop: 'auto'
                  }}
                >
                  — {testimonial.author}
                </p>
              </motion.div>
              ))}
          </S.TestimonialsGrid>
        </S.Container>
      </S.Section>

      {/* Newsletter Section - Rosa Hintergrund */}
      <S.Section
        id="newsletter"
        style={{ 
          background: '#E6007A',
          padding: '80px 0',
          marginTop: '0'
        }}
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true, margin: "-100px" }}
        transition={{ duration: 0.8 }}
      >
        <S.Container>
          <S.SplitContent textColor="white" style={{ maxWidth: '100%' }}>
            <motion.div
              initial={{ opacity: 0, y: 40, scale: 0.9 }}
              whileInView={{ opacity: 1, y: 0, scale: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ 
                duration: 0.9, 
                delay: 0.1,
                ease: [0.4, 0, 0.2, 1]
              }}
            >
              {/* Frage */}
              <motion.p
                className="mobile-text-small"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                  lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                  marginBottom: 'clamp(30px, 4vw, 40px)',
                  color: 'white',
                  fontWeight: 400,
                  whiteSpace: 'pre-line',
                  display: 'block'
                }}
              >
                {data.newsletter.question}
              </motion.p>
              
              {/* CTA Text */}
              <motion.p
                className="mobile-text-small"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                  lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                  marginTop: 'clamp(25px, 3vw, 35px)',
                  marginBottom: 'clamp(30px, 4vw, 40px)',
                  color: 'white',
                  fontWeight: 700,
                  display: 'block'
                }}
              >
                {data.newsletter.cta}
              </motion.p>
              
              {/* Assurance */}
              <motion.p
                className="mobile-text-small"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                  lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                  marginTop: 'clamp(25px, 3vw, 35px)',
                  marginBottom: 'clamp(40px, 5vw, 60px)',
                  color: 'white',
                  fontWeight: 400,
                  display: 'block'
                }}
              >
                {data.newsletter.assurance}
              </motion.p>
              
              {/* Motto */}
              <motion.p
                className="mobile-text-small"
                style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontSize: 'clamp(0.7rem, 1.2vw, 1rem)',
                  lineHeight: 'clamp(1.3, 1.8vw, 1.5)',
                  marginTop: 'clamp(25px, 3vw, 35px)',
                  marginBottom: 'clamp(40px, 5vw, 60px)',
                  color: 'white',
                  fontWeight: 400,
                  whiteSpace: 'pre-line',
                  display: 'block'
                }}
              >
                {data.newsletter.motto}
              </motion.p>
              
              {/* Button */}
               <motion.button
                  className="mobile-text-small"
                  onClick={() => {
                    // Newsletter-Anmeldung - hier könnte ein Formular oder Link eingefügt werden
                    const element = document.querySelector('#contact');
                    if (element) {
                      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                  }}
                  style={{
                    fontFamily: "'Poppins', sans-serif",
                    fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
                    fontWeight: 600,
                    color: '#e80c7f',
                  background: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  padding: 'clamp(10px, 1.5vw, 14px) clamp(20px, 3vw, 28px)',
                  cursor: 'pointer',
                  marginTop: 'clamp(30px, 4vw, 40px)',
                  marginBottom: 'clamp(20px, 3vw, 30px)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  outline: 'none',
                  display: 'block',
                  position: 'relative',
                  overflow: 'hidden',
                  width: 'fit-content',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}
                whileHover={{ 
                  y: -3,
                  scale: 1.05,
                  transition: { duration: 0.3 }
                }}
                whileTap={{ 
                  scale: 0.98,
                  transition: { duration: 0.1 }
                }}
              >
                {data.newsletter.buttonText}
              </motion.button>
              
              {/* Logo unten */}
              <motion.div
                style={{
                  marginTop: 'clamp(50px, 6vw, 80px)',
                  textAlign: 'center'
                }}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <OptimizedImage
                  src={data.newsletter.logo.src}
                  alt={data.newsletter.logo.alt}
                  width={data.newsletter.logo.width}
                  height={data.newsletter.logo.height}
                  sizes={data.newsletter.logo.sizes}
                  loading={data.newsletter.logo.loading as 'lazy' | 'eager'}
                  objectFit="contain"
                  style={{
                    height: 'clamp(60px, 8vw, 100px)',
                    width: 'auto',
                    opacity: 0.9
                  }}
                />
              </motion.div>
            </motion.div>
          </S.SplitContent>
        </S.Container>
      </S.Section>
    </S.PageWrapper>
  );
};

export default Home;

